import type { MyWorkItem } from "@/stores/my-work-store"
import type { Bid } from "@/lib/schemas"

// Shared by HomePage ("Up next") and MyWorkPage — one definition of "what's
// most urgent" so the two surfaces never disagree.

export const STATUS_PRIORITY: Record<string, number> = {
  'reviewer:in_review': 0,
  'writer:changes_requested': 1,
  'writer:in_progress': 2,
  'writer:draft_complete': 3,
  'writer:not_started': 4,
  'writer:in_review': 5,
  'reviewer:not_started': 6,
  'writer:approved': 7,
  'writer:complete': 8,
  'reviewer:approved': 9,
  'reviewer:complete': 10,
}

function priorityOf(item: MyWorkItem): number {
  return STATUS_PRIORITY[`${item.role}:${item.collaborationStatus}`] ?? 99
}

export function sortByPriority(items: MyWorkItem[]): MyWorkItem[] {
  return [...items].sort((a, b) => priorityOf(a) - priorityOf(b))
}

/**
 * Urgency: bid deadline ascending (earliest — i.e. most overdue — first),
 * items on bids with no deadline last, then STATUS_PRIORITY as tiebreak.
 */
export function sortByUrgency(items: MyWorkItem[], bidsById: Map<string, Bid>): MyWorkItem[] {
  return [...items].sort((a, b) => {
    const da = bidsById.get(a.bidId)?.deadline
    const db = bidsById.get(b.bidId)?.deadline
    if (da && db && da !== db) return new Date(da).getTime() - new Date(db).getTime()
    if (da && !db) return -1
    if (!da && db) return 1
    return priorityOf(a) - priorityOf(b)
  })
}

// ===================
// Document order
// ===================

const collator = new Intl.Collator('en-AU', { numeric: true, sensitivity: 'base' })

/**
 * Stand-in for outline order. The My Work payload carries no section index
 * (section keys are random), so rows are ordered by document, then by section
 * title with numbers compared as numbers — "2.10" after "2.9". Questionnaire
 * titles start with the question number, so for those this IS document order.
 */
export function compareDocumentOrder(a: MyWorkItem, b: MyWorkItem): number {
  return collator.compare(a.artifactName, b.artifactName) || collator.compare(a.sectionTitle, b.sectionTitle)
}

export function sortByDocumentOrder(items: MyWorkItem[]): MyWorkItem[] {
  return [...items].sort(compareDocumentOrder)
}

// ===================
// Grouping (My Work)
// ===================

export type MyWorkSortMode = 'urgency' | 'status' | 'bid'

export interface MyWorkBidGroup {
  bidId: string
  bidTitle: string
  items: MyWorkItem[]
}

function compareDeadlines(da: string | null | undefined, db: string | null | undefined): number {
  if (da && db) return new Date(da).getTime() - new Date(db).getTime()
  if (da) return -1
  if (db) return 1
  return 0
}

function bestPriority(group: MyWorkBidGroup): number {
  return Math.min(...group.items.map(priorityOf))
}

/**
 * Group items by bid and order both the groups and the rows in them.
 *
 * - `urgency`: groups by bid deadline (soonest / most overdue first, no deadline
 *   last), then by the group's most pressing status.
 * - `status`: groups by their most pressing status, then deadline; rows inside a
 *   group by status too.
 * - `bid`: groups alphabetically by bid title.
 *
 * Rows keep document order except under `status`.
 */
export function groupMyWork(
  items: MyWorkItem[],
  mode: MyWorkSortMode,
  bidsById: Map<string, Bid>,
): MyWorkBidGroup[] {
  const byBid = new Map<string, MyWorkBidGroup>()
  for (const item of items) {
    let group = byBid.get(item.bidId)
    if (!group) {
      group = { bidId: item.bidId, bidTitle: item.bidTitle, items: [] }
      byBid.set(item.bidId, group)
    }
    group.items.push(item)
  }

  const groups = Array.from(byBid.values()).map((g) => {
    const inDocOrder = sortByDocumentOrder(g.items)
    return { ...g, items: mode === 'status' ? sortByPriority(inDocOrder) : inDocOrder }
  })

  const deadlineOf = (g: MyWorkBidGroup) => bidsById.get(g.bidId)?.deadline
  const byTitle = (a: MyWorkBidGroup, b: MyWorkBidGroup) => collator.compare(a.bidTitle, b.bidTitle)

  return groups.sort((a, b) => {
    if (mode === 'bid') return byTitle(a, b)
    if (mode === 'status') {
      return bestPriority(a) - bestPriority(b)
        || compareDeadlines(deadlineOf(a), deadlineOf(b))
        || byTitle(a, b)
    }
    return compareDeadlines(deadlineOf(a), deadlineOf(b))
      || bestPriority(a) - bestPriority(b)
      || byTitle(a, b)
  })
}
